import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Pill, AlertTriangle } from "lucide-react";
import { formatDate } from "@/lib/date";

export default function PatientMedicationsTab({ patientId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/patients/${patientId}/medication-summary`);
      setSummary(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/ai/patients/${patientId}/medication-summary`);
      setSummary(data);
      toast.success("Medication changes summarized");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to summarize medication changes");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="patient-medications-tab">
      <h2 className="flex items-center gap-2 font-semibold text-slate-900">
        <Pill className="h-4 w-4 text-teal-700" /> Medication Management
      </h2>

      {loading && <p className="mt-6 text-sm text-slate-400">Loading medications...</p>}

      {!loading && (
        <div className="mt-4 rounded-lg border border-slate-200 p-4" data-testid="current-medicines-list">
          <p className="text-xs font-semibold uppercase text-slate-500">Current Medicines</p>
          {summary?.current_medicines?.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {summary.current_medicines.map((m, i) => (
                <li key={`${i}-${m.name}`} className="text-sm text-slate-800">
                  {m.name} {m.dosage && <span className="text-slate-500">— {m.dosage}</span>} {m.frequency && <span className="text-slate-500">({m.frequency})</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-slate-400">No current medicines recorded yet.</p>
          )}
        </div>
      )}

      {summary?.duplicate_medicines?.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="duplicate-medicines-warning">
          <AlertTriangle className="h-4 w-4 flex-none text-red-600" />
          <p className="text-sm text-red-700">Duplicate medicines flagged in a single visit's prescription: {summary.duplicate_medicines.join(", ")}</p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Medication Changes</h3>
          {summary && <p className="text-xs text-slate-500">Last generated {formatDate(summary.generated_at)} by {summary.generated_by}</p>}
        </div>
        <Button data-testid="summarize-medication-changes-btn" size="sm" disabled={generating} onClick={generate} className="gap-2 bg-teal-700 hover:bg-teal-800">
          <Sparkles className="h-3 w-3" /> {generating ? "Summarizing..." : summary ? "Regenerate" : "Summarize Medication Changes"}
        </Button>
      </div>

      {summary?.bullets?.length > 0 && (
        <ul className="mt-3 list-inside list-disc space-y-1 rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-800" data-testid="medication-summary-bullets">
          {summary.bullets.map((b, i) => <li key={`${i}-${b}`}>{b}</li>)}
        </ul>
      )}
      {!loading && !summary && (
        <p className="mt-3 text-sm text-slate-400" data-testid="medication-summary-empty-state">
          Click "Summarize Medication Changes" to generate a short overview of what's changed across visits.
        </p>
      )}
    </div>
  );
}
